// Libraries
import React from 'react';

// Supermove
import {Icon, Styled, Space, IconSource} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {PopoverType, usePopover, useQuery, useResponsive, useToast} from '@supermove/hooks';
import {Conversation, ProjectModel} from '@supermove/models';
import {Environment} from '@supermove/sdk';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import IconButton from '@shared/design/components/IconButton';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import ConversationForm from '@shared/modules/Communication/forms/ConversationForm';
import useUpdateConversationMutation from '@shared/modules/Communication/hooks/useUpdateConversationMutation';
import ProjectWidgetKind, {
  ProjectWidgetKindType,
} from '@shared/modules/Project/enums/ProjectWidgetKind';

const ProjectPaneHeaderContainer = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const goToProjectInOfficeApp = ({
  isStorage,
  projectUuid,
}: {
  isStorage: boolean;
  projectUuid: string;
}) => {
  window.open(
    `${Environment.getAppUrl()}${isStorage ? 'storage/' : ''}/projects/${projectUuid}`,
    '_blank',
  );
};

const getGoToOfficeAppAction = ({projectUuid}: {projectUuid: string}) => {
  return {
    text: Environment.isOfficeApp() ? 'View Project' : 'Go to Office App',
    rightComponent: <IconButton isDisplayOnly isSecondary source={Icon.ArrowUpRightFromSquare} />,
    onPress: () => {
      window.open(`${Environment.getAppUrl()}/projects/${projectUuid}`, '_blank');
    },
  };
};

const HeaderIconButton = ({icon, onPress}: {icon: IconSource; onPress: () => void}) => {
  const responsive = useResponsive();
  const buttonSide = responsive.desktop ? 24 : 32;

  return (
    <IconButton
      source={icon}
      onPress={onPress}
      size={responsive.desktop ? 16 : 20}
      buttonStyle={{height: buttonSide, width: buttonSide}}
      isSecondary
    />
  );
};

const MobileConversationsActionMenu = ({
  projectUuid,
  project,
  actionMenu,
  children,
}: {
  projectUuid: string;
  project: ProjectModel;
  actionMenu: PopoverType;
  children: React.ReactNode;
}) => {
  const responsive = useResponsive();
  const conversation = project.conversations[0];
  const isConversationOpen = conversation.status === Conversation.ConversationStatus.OPEN;

  const successToast = useToast({
    ToastComponent: SuccessToast,
  });

  const {form, handleSubmit, submitting} = useUpdateConversationMutation({
    conversationForm: ConversationForm.edit(conversation),
    onSuccess: () => {
      actionMenu.handleClose();
      successToast.handleToast({
        message: isConversationOpen
          ? `${responsive.desktop ? 'Conversation marked' : 'Marked'} as follow up.`
          : `${responsive.desktop ? 'Conversation marked' : 'Marked'} as resolved.`,
      });
    },
    onError: (error) => console.log({error}),
  });

  const handleToggleStatus = () => {
    form.setFieldValue(
      'conversationForm.status',
      isConversationOpen
        ? Conversation.ConversationStatus.CLOSED
        : Conversation.ConversationStatus.OPEN,
    );
    setTimeout(handleSubmit, 0);
  };

  return (
    <ActionMenuPopover
      popover={actionMenu}
      actions={[
        {
          text: isConversationOpen ? 'Mark as resolved' : 'Mark as follow up',
          onPress: handleToggleStatus,
          isLoading: submitting,
        },
        getGoToOfficeAppAction({projectUuid}),
      ]}
    >
      {children}
    </ActionMenuPopover>
  );
};

const MobileActionMenu = ({
  projectUuid,
  widget,
  project,
  actionMenu,
  children,
}: {
  projectUuid: string;
  widget?: ProjectWidgetKindType;
  project?: ProjectModel;
  actionMenu: PopoverType;
  children: React.ReactNode;
}) => {
  if (widget === ProjectWidgetKind.CONVERSATIONS && project) {
    return (
      <MobileConversationsActionMenu
        projectUuid={projectUuid}
        project={project}
        actionMenu={actionMenu}
      >
        {children}
      </MobileConversationsActionMenu>
    );
  }
  return (
    <ActionMenuPopover popover={actionMenu} actions={[getGoToOfficeAppAction({projectUuid})]}>
      {children}
    </ActionMenuPopover>
  );
};

const ProjectPaneHeader = ({
  projectUuid,
  widget,
  handleCloseProjectPane,
}: {
  projectUuid: string;
  widget?: ProjectWidgetKindType;
  handleCloseProjectPane: () => void;
}) => {
  const responsive = useResponsive();
  const titleText = ProjectWidgetKind.getDisplay(widget);
  const actionMenu = usePopover();

  const {data} = useQuery(ProjectPaneHeader.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectUuid,
    },
  });

  if (responsive.desktop) {
    return (
      <ProjectPaneHeaderContainer>
        <Text.Heading2>{titleText}</Text.Heading2>
        <Space flex={1} />
        {data?.project.organization.isOfficeAppEnabled && (
          <React.Fragment>
            <TertiaryButton
              text={Environment.isOfficeApp() ? 'Project' : 'Office App'}
              onPress={() =>
                goToProjectInOfficeApp({isStorage: data?.project.isStorage, projectUuid})
              }
              isDisabled={!data?.project}
            >
              <Space width={4} />
              <IconButton source={Icon.ArrowUpRightFromSquare} isDisplayOnly size={12} />
            </TertiaryButton>
            <Space width={8} />
          </React.Fragment>
        )}
        <HeaderIconButton icon={Icon.Xmark} onPress={handleCloseProjectPane} />
      </ProjectPaneHeaderContainer>
    );
  }

  return (
    <ProjectPaneHeaderContainer>
      <HeaderIconButton icon={Icon.Xmark} onPress={handleCloseProjectPane} />
      <Space flex={1} />
      <Text.Heading2>{titleText}</Text.Heading2>
      <Space flex={1} />
      <MobileActionMenu
        projectUuid={projectUuid}
        project={data?.project}
        widget={widget}
        actionMenu={actionMenu}
      >
        <IconButton source={Icon.EllipsisV} onPress={actionMenu.handleOpen} isSecondary />
      </MobileActionMenu>
    </ProjectPaneHeaderContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectPaneHeader.query = gql`
  ${ConversationForm.edit.fragment}

  query ProjectPaneHeader($projectUuid: String!) {
    project(uuid: $projectUuid) {
      id
      isStorage
      organization {
        id
        isOfficeAppEnabled
      }
      conversations {
        id
        status
        ...ConversationForm_edit
      }
    }
  }
`;

export default ProjectPaneHeader;
